import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import {
  HD_NODAL_LINES,
  HdNodalLineSide,
} from '@/app/human-design/chart/bodygraph/nodal-lines/hd-nodal-line-card.component';

/** Eén kaart-plek in de deck: lijn + kant; de kaart zelf haalt de content op. */
interface NodalLineCardRef {
  readonly line: number;
  readonly side: HdNodalLineSide;
}

/**
 * Pagina die de Nodal lines cheat sheet toont, nagebouwd uit het Figma-design
 * "A3 HD Nodal Lines" (zonder A3-header/branding-footer). Per lijn een
 * design-kaart ("Waar we zijn": de omgeving/het pad) en een personaliteits-
 * kaart ("Wat we zien": het perspectief op die omgeving), gerenderd door het
 * gedeelde {@link HdNodalLineCardComponent} (ook gebruikt in het nodale-
 * omgevingen-paneel).
 *
 * Content komt uit de `human-design/nodal-lines`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}; taalwissel via de gedeelde
 * content-taal-picker, nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-nodal-lines-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-nodal-lines-cheat-sheet-page.component.html',
  styleUrl: './hd-nodal-lines-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdNodalLinesCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));

  /** Kaarten in Figma-volgorde: per lijn eerst design, dan personaliteit. */
  protected readonly cards: readonly NodalLineCardRef[] = HD_NODAL_LINES.flatMap(line => [
    { line, side: 'design' as const },
    { line, side: 'personality' as const },
  ]);

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('nodalLines', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('nodalLines', key, 'nl');
  }
}
